import type {
  Download,
  FetchData,
  FetchDataWithError,
} from '@hooks/useDataFetcher';
import { GLOBAL_STORE } from '@shared/constants';
import { toMap } from '@shared/helpers';
import { useState } from 'react';
import type { AuthorizationConfigurationV1 } from 'types/assortment-admin';
import type { ICar } from 'types/custom';
import type {
  addonadmin_ListAddOnPackagesResponse,
  addonadmin_ListAddonsResponse,
  admin_addonadmin_AddOn,
  admin_addonadmin_AddOnPackage,
  admin_caradmin_ArchiveLiveCarOfferPayload,
  admin_caradmin_ScheduleCarOffersPayload,
  admin_caradmin_UpdateBTOStatusPayload,
  admin_caradmin_UpdateScheduledOfferLiveDatePayload,
} from 'types/offers';

export type UseOffers = {
  cars: { loading: boolean; map: Map<string, ICar[]> };
  addOns: { loading: boolean; list: admin_addonadmin_AddOn[] };
  addOnPackages: { loading: boolean; list: admin_addonadmin_AddOnPackage[] };
  toggleBTO: (
    storeId: string,
    btoEnabled: boolean,
    carIds: string[],
    market?: string,
  ) => Promise<void>;
  getCarsByStoreId: (storeId: string, market?: string) => Promise<ICar[]>;
  getCarById: (carId: string, market?: string) => Promise<ICar | undefined>;
  getAddOns: () => Promise<admin_addonadmin_AddOn[] | undefined>;
  getAddOnOPackages: () => Promise<admin_addonadmin_AddOnPackage[] | undefined>;
  getOffersCSVByDate: (
    market: string,
    startDate: string,
    endDate: string,
  ) => Promise<boolean>;
  getAuthConfig: () => Promise<AuthorizationConfigurationV1[]>;
  publishScheduledOffer: (
    storeId: string,
    carId: string,
    scheduledOfferId: string,
    market?: string,
  ) => Promise<ICar | undefined>;
  scheduleOffer: (
    body: admin_caradmin_ScheduleCarOffersPayload,
    market?: string,
  ) => Promise<ICar[]>;
  archiveLiveOffers: (
    storeId: string,
    body: admin_caradmin_ArchiveLiveCarOfferPayload[],
    market?: string,
  ) => Promise<ICar[]>;
  updateLiveDate: (
    body: admin_caradmin_UpdateScheduledOfferLiveDatePayload[],
    market?: string,
  ) => Promise<ICar[]>;
  cancelScheduledOffer: (
    storeId: string,
    carId: string,
    scheduledOfferId: string,
    market?: string,
  ) => Promise<ICar | undefined>;
};

interface IState {
  cars: UseOffers['cars'];
  addOns: UseOffers['addOns'];
  addOnPackages: UseOffers['addOnPackages'];
  authConfig: { loading: boolean; list: AuthorizationConfigurationV1[] };
}

export const useOffers = (
  fetchDataWithError: FetchDataWithError,
  fetchData: FetchData,
  download: Download,
): UseOffers => {
  const [state, setState] = useState<IState>({
    addOns: { loading: false, list: [] },
    cars: { loading: false, map: new Map() },
    authConfig: { loading: false, list: [] },
    addOnPackages: { list: [], loading: false },
  });

  const getCarById: UseOffers['getCarById'] = async (carId, market) => {
    if (!carId) {
      return;
    }

    const car = [...state.cars.map.values()]
      .flat()
      .find((car) => car.id === carId);

    if (car) {
      return car;
    }

    setState((state) => ({
      ...state,
      cars: { ...state.cars, loading: true },
    }));

    const res = await fetchData<ICar>(`/api/offers/v2/admin/cars/${carId}`, {
      market,
    });

    setState((state) => ({
      ...state,
      cars: { ...state.cars, loading: false },
    }));

    if (res.id) {
      return res;
    }
  };

  const getCarsByStoreId: UseOffers['getCarsByStoreId'] = async (
    storeId,
    market,
  ) => {
    if (!storeId || storeId === GLOBAL_STORE.id) {
      return [];
    }

    if (state.cars.map.get(storeId)?.length) {
      return state.cars.map.get(storeId) ?? [];
    }

    setState((state) => ({
      ...state,
      cars: { ...state.cars, loading: true },
    }));

    const { data: cars = [] } = await fetchDataWithError<ICar[]>(
      `/api/offers/v2/admin/stores/${storeId}/cars${market ? `?market=${market}` : ''}`,
    );

    const map = new Map(state.cars.map);
    map.set(storeId, cars);

    setState((state) => ({
      ...state,
      cars: { ...state.cars, map, loading: false },
    }));

    return cars;
  };

  const toggleBTO: UseOffers['toggleBTO'] = async (
    storeId,
    btoEnabled,
    carIDs,
    market,
  ) => {
    try {
      // Optimistic update
      const map = new Map(state.cars.map);
      map.set(
        storeId,
        (map.get(storeId) ?? []).map((car) =>
          carIDs.findIndex((carId) => carId === car.id) === -1
            ? car
            : { ...car, btoEnabled },
        ),
      );
      setState((prev) => ({ ...prev, cars: { ...prev.cars, map } }));

      const { error } = await fetchDataWithError(
        `/api/offers/v2/admin/cars/bto-status${market ? `?market=${market}` : ''}`,
        undefined,
        'POST',
        { carIDs, btoEnabled } as admin_caradmin_UpdateBTOStatusPayload,
        {
          error: 'Failed to update BTO status',
          success: 'BTO status updated successfully',
        },
      );

      if (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      const map = new Map(state.cars.map);
      map.set(
        storeId,
        (map.get(storeId) ?? []).map((car) =>
          carIDs.findIndex((carId) => carId === car.id) === -1
            ? car
            : { ...car, btoEnabled: !btoEnabled },
        ),
      );
      setState((prev) => ({ ...prev, cars: { ...prev.cars, map } }));
    }
  };

  const scheduleOffer: UseOffers['scheduleOffer'] = async (body, market) => {
    setState((state) => ({
      ...state,
      cars: { ...state.cars, loading: true },
    }));

    const updatedCars = await fetchData<ICar[]>(
      '/api/offers/v2/admin/cars/schedule-offers',
      { market },
      'POST',
      body,
      true,
    );

    if (updatedCars.length) {
      const map = new Map(state.cars.map);
      map.set(
        body.storeID,
        (map.get(body.storeID) ?? []).map(
          (car) => updatedCars.find((c) => c.id === car.id) ?? car,
        ),
      );

      setState((state) => ({
        ...state,
        cars: { ...state.cars, map, loading: false },
      }));

      return updatedCars;
    }

    setState((state) => ({
      ...state,
      cars: { ...state.cars, loading: false },
    }));

    return [];
  };

  const publishScheduledOffer: UseOffers['publishScheduledOffer'] = async (
    storeId,
    carId,
    scheduledOfferId,
    market,
  ) => {
    setState((state) => ({
      ...state,
      cars: { ...state.cars, loading: true },
    }));

    const updatedCar = await fetchData<ICar>(
      `/api/offers/v2/admin/cars/${carId}/scheduled-offers/${scheduledOfferId}/publish?${market ? `?market=${market}` : ''}`,
      undefined,
      'POST',
    );

    const map = new Map(state.cars.map);

    if (updatedCar.id) {
      map.set(
        storeId,
        (map.get(storeId) ?? []).map((car) =>
          car.id !== carId ? car : updatedCar,
        ),
      );
    }

    setState((state) => ({
      ...state,
      cars: { ...state.cars, map, loading: false },
    }));

    if (updatedCar.id) {
      return updatedCar;
    }
  };

  const cancelScheduledOffer: UseOffers['cancelScheduledOffer'] = async (
    storeId,
    carId,
    scheduledOfferId,
    market,
  ) => {
    setState((state) => ({
      ...state,
      cars: { ...state.cars, loading: true },
    }));

    const updatedCar = await fetchData<ICar>(
      `/api/offers/v2/admin/cars/${carId}/scheduled-offers/${scheduledOfferId}/cancel?${market ? `?market=${market}` : ''}`,
      undefined,
      'POST',
    );

    const map = new Map(state.cars.map);

    if (updatedCar.id) {
      map.set(
        storeId,
        (map.get(storeId) ?? []).map((car) =>
          carId !== car.id ? car : updatedCar,
        ),
      );
    }

    setState((state) => ({
      ...state,
      cars: { ...state.cars, map, loading: false },
    }));

    if (updatedCar.id) {
      return updatedCar;
    }
  };

  const archiveLiveOffers: UseOffers['archiveLiveOffers'] = async (
    storeId,
    body,
    market,
  ) => {
    setState((state) => ({
      ...state,
      cars: { ...state.cars, loading: true },
    }));

    const updatedCars = await fetchData<ICar[]>(
      `/api/offers/v2/admin/cars/live-offers/archive${market ? `?market=${market}` : ''}`,
      undefined,
      'POST',
      body,
    );

    const map = new Map(state.cars.map);

    if (updatedCars.length) {
      const storeCars = map.get(storeId) ?? [];

      for (const car of updatedCars) {
        map.set(
          storeId,
          storeCars.map((c) => (c.id === car.id ? car : c)),
        );
      }
    }

    setState((state) => ({
      ...state,
      cars: { ...state.cars, map, loading: false },
    }));

    return updatedCars.length ? updatedCars : [];
  };

  const updateLiveDate: UseOffers['updateLiveDate'] = async (body, market) => {
    setState((state) => ({ ...state, cars: { ...state.cars, loading: true } }));

    const updatedCars = await fetchData<ICar[]>(
      `/api/offers/v2/admin/cars/scheduled-offers/live-dates${market ? `?market=${market}` : ''}`,
      undefined,
      'POST',
      body,
      true,
    );

    const updatedCarsById = toMap([...updatedCars].reverse(), 'id');

    const map = new Map(state.cars.map);

    for (const [storeId, cars] of map.entries()) {
      map.set(
        storeId,
        cars.map((car) => updatedCarsById.get(car.id) ?? car),
      );
    }

    setState((state) => ({
      ...state,
      cars: { ...state.cars, map, loading: false },
    }));

    return updatedCars;
  };

  const getAddOns: UseOffers['getAddOns'] = async () => {
    setState((state) => ({
      ...state,
      addOns: { ...state.addOns, loading: true },
    }));

    const addOnResponse = (await fetchData(
      `/api/offers/add-ons`,
      undefined,
      undefined,
      undefined,
      { error: true, warning: true },
    )) as addonadmin_ListAddonsResponse;

    if (addOnResponse?.data?.addOns) {
      setState((state) => ({
        ...state,
        addOns: {
          ...state.addOns,
          loading: false,
          list: addOnResponse?.data?.addOns ?? [],
        },
      }));

      return addOnResponse?.data?.addOns ?? [];
    }
  };

  const getAddOnOPackages: UseOffers['getAddOnOPackages'] = async () => {
    setState((state) => ({
      ...state,
      addOns: { ...state.addOns, loading: true },
    }));

    const addOnResponse = (await fetchData(
      `/api/offers/add-on-packages`,
      undefined,
      undefined,
      undefined,
      { error: true, warning: true },
    )) as addonadmin_ListAddOnPackagesResponse;

    if (addOnResponse?.data?.addOnPackages) {
      setState((state) => ({
        ...state,
        addOnPackages: {
          ...state.addOnPackages,
          loading: false,
          list: addOnResponse?.data?.addOnPackages ?? [],
        },
      }));

      return addOnResponse?.data?.addOnPackages ?? [];
    }
  };

  const getOffersCSVByDate: UseOffers['getOffersCSVByDate'] = async (
    market,
    startDate,
    endDate,
  ) => {
    setState((state) => ({
      ...state,
      cars: { ...state.cars, loading: true },
    }));
    await download(
      `/api/offers/markets/${market}/offers-csv-by-date?startDate=${startDate}&endDate=${endDate}`,
      `offers-${market}-${startDate}-till-${endDate}.csv`,
    );
    setState((state) => ({
      ...state,
      cars: { ...state.cars, loading: false },
    }));
    return true;
  };

  return {
    getAddOns,
    toggleBTO,
    getCarById,
    scheduleOffer,
    updateLiveDate,
    cars: state.cars,
    getCarsByStoreId,
    archiveLiveOffers,
    getAddOnOPackages,
    getOffersCSVByDate,
    addOns: state.addOns,
    cancelScheduledOffer,
    publishScheduledOffer,
    addOnPackages: state.addOnPackages,
    getAuthConfig: async () => {
      if (state.authConfig.list.length) {
        return state.authConfig.list;
      }

      setState((state) => ({
        ...state,
        authConfig: { ...state.authConfig, loading: true },
      }));

      const res = await fetchData<{
        status: number;
        data: AuthorizationConfigurationV1[];
      }>('/api/offers/auth');

      setState((state) => ({
        ...state,
        authConfig: { ...state.authConfig, list: res?.data, loading: false },
      }));

      return res?.data;
    },
  };
};
